import { Box, Theme, Typography } from '@mui/material'
import ShowToRole from 'components/Common/Portal/ShowToRole'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import {
  ProjectStateAsString,
  ProjectStatus,
} from 'controllers/Project/ProjectStateMachine'
import { ProjectProcessFlow } from 'features/BillOfMaterials/ProjectHeader/ProjectProcessFlow'
import { ProjectVisibleInPortal } from 'features/BillOfMaterials/ProjectHeader/ProjectVisibleInPortal'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { projectStatusSelector } from 'features/BillOfMaterials/store/selectors/projectStatusSelector'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RootAppState, useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { BomItemErpExportErrorFilter } from '../BomItemErpExportFilter'
import { InProductionProjectStatuses } from './InProductionProjectStatuses'
import './ProjectStatus.scss'
import { CancelledState } from './States/CancelledState'
import { ProductionStates } from './States/CurrentState'
import { DefiningState } from './States/DefiningState'
import { OrderState } from './States/OrderState'
import { QuotedState } from './States/QuoteState'
import { QuotingState } from './States/QuotingState'
import { RequestState } from './States/RequestState'

const useStyles = makeStyles()((theme: Theme) => {
  return {
    root: {
      position: 'relative',
      '& li': {
        clipPath:
          'polygon(calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 2px 100%, 10px 50%, 2px 0)',
        marginLeft: '-7px',
        '&:first-of-type': {
          clipPath:
            'polygon(calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 2px 100%, 0px 100%, 0px 0)',
          marginLeft: '0px',
        },
      },
    },
    nestingOrEstimating: {
      pointerEvents: 'none',
      outline: `1px dotted ${theme.palette.divider}`,
      borderRadius: '4px',
      outlineOffset: '-1px',

      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          ${theme.palette.primary.main} 0,
          ${theme.palette.primary.dark} 5px,
          transparent 10px,
          transparent 15px
          )`,
        opacity: 0.2,
        zIndex: 1,
        backgroundSize: '200% 200%',
        animation: 'moveDiagonal 50s linear infinite',
      },
      '& li': {
        backgroundColor: 'transparent !important',
        color: theme.palette.text.disabled,
        '&:before': {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        },
      },
      // backgroundColor: theme.palette.action.disabled,
      // '& li': {
      //   pointerEvents: 'none',
      //   // backgroundColor: theme.palette.action.disabled,
      //   '&:before': {
      //     backgroundColor: theme.palette.action.disabled,
      //     backgroundImage: 'none',
      //   },
      // },
      // '& li:hover': {
      //   backgroundColor: theme.palette.action.disabled,
      // },
    },
  }
})

export const ProjectStatusSelector = (props: {
  bomItemPointer: BomItemPointer
}) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const isNesting = useAppSelector(
    (state: RootAppState) => state.project.projectOperations.isNesting,
    isEqual,
  )

  const isEstimating = useAppSelector(
    (state: RootAppState) => state.project.projectOperations.isEstimating,
    isEqual,
  )

  const currentProjectStateFromStore = useAppSelector(
    projectStatusSelector,
    isEqual,
  )

  const createdInPortal = useAppSelector(
    (state: RootAppState) => state.project.activeProject?.createdInPortal,
  )

  const isBuyerView = useAppSelector(
    projectSelectors.projectInBuyerViewSelector,
  )

  const OperationDescription = useMemo(() => {
    if (isBuyerView) {
      return null
    }

    if (isNesting) {
      return (
        <LocalizedTypography
          translationKey="project:nesting"
          suffix="..."
          variant="caption"
          color="textSecondary"
        />
      )
    } else if (isEstimating) {
      return (
        <LocalizedTypography
          translationKey="project:estimating"
          suffix="..."
          variant="caption"
          color="textSecondary"
        />
      )
    }
    return null
  }, [isBuyerView, isNesting, isEstimating])

  if (currentProjectStateFromStore === ProjectStateAsString.CANCELLED) {
    return (
      <>
        <div className="project-status">
          <CancelledState />
        </div>
      </>
    )
  }

  const StatesToShow: React.ReactElement<any>[] = []

  if (InProductionProjectStatuses.includes(currentProjectStateFromStore)) {
    StatesToShow.push(
      <ProductionStates
        key={ProjectStatus.ORDERED + '_production'}
        showStatus={ProjectStatus.ORDERED}
      />,
    )
    StatesToShow.push(
      <ProductionStates
        key={ProjectStatus.PRODUCING}
        showStatus={ProjectStatus.PRODUCING}
      />,
    )
    StatesToShow.push(
      <ProductionStates
        key={ProjectStatus.PRODUCED}
        showStatus={ProjectStatus.PRODUCED}
      />,
    )
    StatesToShow.push(
      <ProductionStates
        key={ProjectStatus.PACKAGING}
        showStatus={ProjectStatus.PACKAGING}
      />,
    )
    StatesToShow.push(
      <ProductionStates
        key={ProjectStateAsString.DELIVERING}
        showStatus={ProjectStatus.DELIVERING}
      />,
    )
  } else {
    StatesToShow.push(<DefiningState key={ProjectStatus.DEFINING} />)
    StatesToShow.push(<RequestState key={ProjectStatus.REQUESTED} />)
    StatesToShow.push(<QuotingState key={ProjectStatus.QUOTING} />)
    StatesToShow.push(<QuotedState key={ProjectStatus.QUOTED} />)
    StatesToShow.push(<OrderState key={ProjectStatus.ORDERED} />)
  }

  // const isInNestingOrEstimating = true
  const isInNestingOrEstimating = isNesting || isEstimating

  return (
    <>
      <div className="project-status">
        <ul
          className={cx(classes.root, {
            [classes.nestingOrEstimating]: isInNestingOrEstimating,
          })}
        >
          {StatesToShow}
        </ul>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'space-between',
            gap: 2,
            paddingRight: 2,
            alignItems: 'center',
          }}
        >
          {createdInPortal && (
            <ShowToRole seller>
              <Typography variant="caption" style={{ marginLeft: '1rem' }}>
                {t('project:created-in-portal', 'self-service portal')}
              </Typography>
            </ShowToRole>
          )}
          <ProjectVisibleInPortal />
          <BomItemErpExportErrorFilter bomItemPointer={props.bomItemPointer} />
          <ProjectProcessFlow />
          {OperationDescription}
        </Box>
      </div>
    </>
  )
}
